import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  CircularProgress,
  Switch,
  FormControlLabel,
  Alert,
  Snackbar,
  Divider,
} from '@mui/material';
import axios from 'axios';
import { Work, Business, LocationOn, Person } from '@mui/icons-material';

function SuperAdminJobs() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const fetchJobs = async () => {
    try {
      const response = await axios.get('/api/super-admin/jobs');
      setJobs(response.data);
    } catch (error) {
      console.error('Error fetching jobs:', error);
      setSnackbar({ open: true, message: 'Failed to fetch jobs', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchJobs();
  }, []);

  const handleToggleStatus = async (jobId, currentStatus) => {
    try {
      await axios.patch(`/api/super-admin/jobs/${jobId}/status`, { is_active: !currentStatus });
      setJobs(jobs.map(job => job.id === jobId ? { ...job, is_active: !currentStatus } : job));
      setSnackbar({ open: true, message: `Job ${!currentStatus ? 'activated' : 'deactivated'} successfully`, severity: 'success' });
    } catch (error) {
      console.error('Error updating job status:', error);
      setSnackbar({ open: true, message: 'Failed to update job status', severity: 'error' });
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container sx={{ py: 4 }}>
      <Typography variant="h4"
                  sx={{
                    color: "#4F46E5",
                    fontWeight: "bold",
                    mb: 2
                  }}
      >
        All Jobs
      </Typography>

      <Grid container spacing={3} sx={{ mt: 2 }}>
        {jobs.map((job) => (
          <Grid item xs={12} md={6} lg={4} key={job.id}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ flexGrow: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
                    {job.title}
                  </Typography>
                  <Chip
                    label={job.is_active ? 'Active' : 'Deactivated'}
                    color={job.is_active ? 'success' : 'error'}
                    size="small"
                  />
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, color: 'text.secondary' }}>
                  <Business sx={{ fontSize: 20, mr: 1 }} />
                  <Typography variant="body2">{job.company_name || job.companyName}</Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, color: 'text.secondary' }}>
                  <LocationOn sx={{ fontSize: 20, mr: 1 }} />
                  <Typography variant="body2">{job.location}</Typography>
                </Box>

                <Divider sx={{ my: 1.5 }} />

                <Typography variant="body2" color="text.secondary" sx={{
                  display: '-webkit-box',
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  mb: 2
                }}>
                  {job.description}
                </Typography>
              </CardContent>
              <CardActions sx={{ p: 2, pt: 0, justifyContent: 'space-between' }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={job.is_active}
                      onChange={() => handleToggleStatus(job.id, job.is_active)}
                      color="primary"
                    />
                  }
                  label={job.is_active ? "Active" : "Deactivated"}
                />
              </CardActions>
            </Card>
          </Grid>
        ))}
        {jobs.length === 0 && (
          <Grid item xs={12}>
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography color="text.secondary">No jobs found in the system.</Typography>
            </Box>
          </Grid>
        )}
      </Grid>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default SuperAdminJobs;
